import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import AuthShell from '../components/AuthShell';

export default function ForgotPassword() {
  const [mode, setMode] = useState('email'); // 'email' | 'code'
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [codeForm, setCodeForm] = useState({ code: '', password: '', confirm: '' });
  const { login } = useAuth();
  const nav = useNavigate();

  async function handleEmailSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post('/auth/forgot-password', { email });
      setSent(true);
      toast.success(r.data.message || 'Check your email for a reset link');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Something went wrong');
    }
    setLoading(false);
  }

  async function handleCodeSubmit(e) {
    e.preventDefault();
    if (codeForm.password !== codeForm.confirm) return toast.error('Passwords do not match');
    if (codeForm.password.length < 6) return toast.error('Password must be at least 6 characters');
    setLoading(true);
    try {
      const r = await api.post('/auth/reset-with-code', {
        email, code: codeForm.code, password: codeForm.password,
      });
      login(r.data.token, r.data.user);
      toast.success('Password updated — you are signed in');
      nav('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Reset failed');
    }
    setLoading(false);
  }

  return (
    <AuthShell
      eyebrow="Account recovery"
      title="Reset your password"
      subtitle={mode === 'email'
        ? "Enter your email and we'll send you a secure reset link."
        : 'Use the reset code provided by your administrator.'}
      compact
    >

        {/* Mode switch */}
        <div className="auth-mode-switch">
          {[['email', 'Email link'], ['code', 'Admin code']].map(([m, label]) => (
            <button key={m} type="button"
              className={`btn btn-sm ${mode === m ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setMode(m)}>
              {label}
            </button>
          ))}
        </div>

        {mode === 'email' ? (
          sent ? (
            <div className="auth-success">
              <span aria-hidden="true">✓</span>
              <p>
                If an account exists for <strong>{email}</strong>, a password reset link is on its way.
                The link expires in 1 hour.
              </p>
            </div>
          ) : (
            <form onSubmit={handleEmailSubmit}>
              <div className="form-group auth-field">
                <label className="label">Email address</label>
                <input className="input" type="email" required autoFocus
                  value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com" />
              </div>
              <button type="submit" className={'btn btn-primary btn-lg btn-block' + (loading ? ' is-loading' : '')} disabled={loading}>
                {loading ? 'Sending…' : 'Send reset link'}
              </button>
            </form>
          )
        ) : (
          <form onSubmit={handleCodeSubmit}>
            <div className="form-group auth-field">
              <label className="label">Email address</label>
              <input className="input" type="email" required
                value={email} onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com" />
            </div>
            <div className="form-group auth-field">
              <label className="label">Reset code</label>
              <input className="input" required autoFocus
                value={codeForm.code}
                onChange={e => setCodeForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                placeholder="XXXX-XXXX"
                style={{ letterSpacing: 2, textTransform: 'uppercase', fontFamily: 'monospace' }} />
            </div>
            <div className="form-group auth-field">
              <label className="label">New password</label>
              <input className="input" type="password" required
                value={codeForm.password} onChange={e => setCodeForm(f => ({ ...f, password: e.target.value }))}
                placeholder="••••••••" />
            </div>
            <div className="form-group auth-field">
              <label className="label">Confirm new password</label>
              <input className="input" type="password" required
                value={codeForm.confirm} onChange={e => setCodeForm(f => ({ ...f, confirm: e.target.value }))}
                placeholder="••••••••" />
            </div>
            <button type="submit" className={'btn btn-primary btn-lg btn-block' + (loading ? ' is-loading' : '')} disabled={loading}>
              {loading ? 'Resetting…' : 'Reset password'}
            </button>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 10, textAlign: 'center' }}>
              Codes expire 1 hour after they're issued.
            </p>
          </form>
        )}

        <p className="auth-switch">
          <Link to="/login">← Back to sign in</Link>
        </p>
    </AuthShell>
  );
}
